import { ERC721_FACTORY } from "@/common/constants";
import { ethers } from "ethers";
import erc721TokenJson from "@/common/constants/abis/ERC721Whitelabel.json";
import erc721FactoryJson from "@/common/constants/abis/ERC721WhitelabelFactoryC2.json";
import { useWallet, Wallet } from "@/common/composables/useWallet";

const tokenIdFor = (creator: string) => {
  const now = Date.now();
  const rand = (Math.random() * 10 ** 18).toString().slice(0, 11);
  return BigInt(creator + now + rand).toString(10);
};
/**
 * exposes an interface for interaction with ethereum smart contract
 */
class ERC721Interface {
  /**
   * Mint ERC721 Token
   * @param tokenURI ipfs metadata uri formatted as ipfs//{CID}
   */
  async mint(tokenURI: string, collection: any): Promise<ethers.Transaction> {
    try {
      const wallet = useWallet();
      const address = await this.getAddress(collection);
      const signer = await wallet.getSigner();
      const creator = await signer.getAddress();

      const args = [
        tokenIdFor(creator), // market id
        tokenURI, // market uri
        [[creator, 10000]], // Array<{payee.address, payee.shares}> on mint
        [], // Array<{creator.address, creator.royalties}> on transfer
        ["0x"], //Array<signature> lazy minting
      ];

      const contract = new ethers.Contract(
        address,
        erc721TokenJson.abi,
        signer
      );
      const signedContract = contract.connect(signer);

      const tx = await signedContract.mintAndTransfer(args, creator);
      await tx.wait();
      return tx;
    } catch (e: any) {
      console.error("Error minting token", e);
      throw e;
    }
  }

  /**
   * Create ERC721 Collection
   * @param name
   * @param symbol
   * @param baseUri
   * @param contractUri
   */
  async createToken(
    name: string,
    symbol: string,
    baseUri: string,
    contractUri: string
  ): Promise<{
    name: string;
    symbol: string;
    baseUri: string;
    contractUri: string;
    salt: ethers.BigNumber;
  }> {
    try {
      const wallet = useWallet();
      const signer = await wallet.getSigner();
      const salt = ethers.BigNumber.from(ethers.utils.randomBytes(32));

      const contract = new ethers.Contract(
        ERC721_FACTORY,
        erc721FactoryJson.abi,
        signer
      );

      const signedContract = contract.connect(signer);

      const tx = await signedContract[
        "createToken(string,string,string,string,uint256)"
      ](name, symbol, baseUri, contractUri, salt);

      await tx.wait();

      return {
        name,
        symbol,
        baseUri,
        contractUri,
        salt,
      };
    } catch (e: any) {
      console.error("Error creating token", e);
      throw new Error(e.toString());
    }
  }

  async getAddress({ name, symbol, contractUri, baseUri, salt }: any) {
    const wallet = useWallet();
    const provider = wallet.getProvider();
    const contract = new ethers.Contract(
      ERC721_FACTORY,
      erc721FactoryJson.abi,
      provider
    );

    return contract["getAddress(string,string,string,string,uint256)"](
      name,
      symbol,
      baseUri,
      contractUri,
      salt
    );
  }
}
export default ERC721Interface;
